import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';

import { apiClient } from '../axios.js';
import { type DetectionGroup, detectionGroupSchema } from '../types/detection-group';

export interface DetectionGroupsParams {
  deviceId: string;
  page?: number;
  pageSize?: number;
  status?: string | string[];
  from?: string;
  to?: string;
  parentDetectionGroupId?: string;
  includeParent?: boolean;
  parentDetectionGroupCreatedAt?: string;
}

export interface UseDetectionGroupsOptions {
  staleTime?: number;
  refetchInterval?: number | false;
  refetchOnWindowFocus?: boolean;
  refetchOnReconnect?: boolean;
  enabled?: boolean;
}

export interface DetectionGroupsResponse {
  groups: DetectionGroup[];
  total: number;
}

/**
 * The call site that makes the schema's ingress guards real. Never throws: a group that fails
 * validation is passed through and reported, so schema drift cannot blank the table. Exported
 * because the WebSocket handlers write into the same query cache.
 */
export const parseDetectionGroup = (group: DetectionGroup): DetectionGroup => {
  const parsed = detectionGroupSchema.safeParse(group);
  if (parsed.success) {
    return parsed.data;
  }

  Sentry.captureException(parsed.error, {
    extra: { detectionGroupId: (group as { id?: unknown })?.id },
  });
  return group;
};

const fetchDetectionGroups = async (
  params: DetectionGroupsParams,
): Promise<DetectionGroupsResponse> => {
  const {
    deviceId,
    page = 1,
    pageSize = 20,
    status,
    from,
    to,
    parentDetectionGroupId,
    includeParent,
    parentDetectionGroupCreatedAt,
  } = params;
  const searchParams = new URLSearchParams();

  searchParams.append('page', String(page));
  searchParams.append('pageSize', String(pageSize));
  if (status) {
    const statuses = Array.isArray(status) ? status : [status];
    statuses.forEach((value) => {
      if (value) {
        searchParams.append('status', value);
      }
    });
  }
  if (from) {
    searchParams.append('from', from);
  }
  if (to) {
    searchParams.append('to', to);
  }
  if (parentDetectionGroupId) {
    searchParams.append('parentDetectionGroupId', parentDetectionGroupId);
  }
  if (includeParent) {
    searchParams.append('includeParent', 'true');
  }
  if (parentDetectionGroupCreatedAt) {
    searchParams.append('parentDetectionGroupCreatedAt', parentDetectionGroupCreatedAt);
  }

  const query = searchParams.toString();
  const url = query
    ? `/devices/${deviceId}/detection-groups?${query}`
    : `/devices/${deviceId}/detection-groups`;

  try {
    const { data } = await apiClient.get<DetectionGroupsResponse>(url);
    return {
      groups: (data?.groups ?? []).map(parseDetectionGroup),
      total: data?.total ?? 0,
    };
  } catch (error) {
    Sentry.captureException(error);
    console.debug({ error });
    throw new Error('Failed to fetch detections');
  }
};

export const useDetectionGroups = (
  params: DetectionGroupsParams,
  options: UseDetectionGroupsOptions = {},
) => {
  const {
    staleTime = 0,
    refetchInterval = 30_000,
    refetchOnWindowFocus = true,
    refetchOnReconnect = true,
    enabled = true,
  } = options;

  return useQuery({
    queryKey: ['detection-groups', params],
    queryFn: () => fetchDetectionGroups(params),
    staleTime,
    refetchInterval,
    refetchOnWindowFocus,
    refetchOnReconnect,
    enabled,
  });
};
